import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./mcUserMenu.css";
import "./mcTheme.css";
import { signOut } from "firebase/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { getAuthClient } from "../../firebaseConfig";
import { useAuth } from "../../auth/AuthProvider";
import { clearActiveApp, hasLeagueAccess, setActiveApp } from "../../auth/appAccess";
import {
  buildProfileDisplayName,
  clearCurrentProfile,
  formatStoreLabel,
  isAdminProfile,
  setAdminStoreView,
  useAdminStoreView,
  useCurrentMcProfile,
} from "../utils/profileAccess";
import MorpheusCountSessionDetail from "../pages/MorpheusCountSessionDetail";
import McDevTrafficPanel from "./McDevTrafficPanel";
import { getMcTrafficProbeRouteContext } from "../utils/mcTrafficProbe";
import { buildDefaultState, loadArchivedSessionRecords, loadEngineState, loadSessionRecords, MC_SESSION_EVENT } from "../utils/mcEngineData";
import { MC_SYNC_EVENT, useMcCloudSync } from "../utils/mcCloudSync";
import { MC_SITE_EVENT, detectSiteHeaders, labelFromSiteValue, loadSites, saveSites, ensureSitesFromHeaders, inventoryHeaderFromSiteName, resolveSite } from "../utils/mcOrgData";
import { clearRememberedOperationalDate } from "../utils/mcSessionDateMemory";
import { loadActiveInventorySnapshot, peekActiveInventorySnapshot, MC_ACTIVE_INVENTORY_EVENT } from "../utils/mcActiveInventory";

function hasExplicitNeoAccess(claims = {}) {
  const aliases = ["neo", "cashup", "cash-up", "neo_admin"];
  const apps = claims?.apps;
  if (Array.isArray(apps)) {
    return apps.map((app) => String(app).toLowerCase()).some((app) => aliases.includes(app));
  }
  if (apps && typeof apps === "object") {
    return aliases.some((alias) => apps[alias] === true);
  }
  return aliases.some((alias) => claims?.[alias] === true || claims?.[`app_${alias}`] === true);
}

export default function MorpheusCountChrome({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, claims, member } = useAuth();
  const mcSyncReady = useMcCloudSync({ userEmail: user?.email || claims?.email || "", uid: user?.uid || claims?.authUid || "", claims: claims || {} });
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dataSettling, setDataSettling] = useState(true);
  const [hoveredAction, setHoveredAction] = useState("");
  const [siteVersion, setSiteVersion] = useState(0);
  const [softRefreshing, setSoftRefreshing] = useState(false);
  const [durableAllHeaders, setDurableAllHeaders] = useState([]);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const currentProfile = useCurrentMcProfile();
  const [mcTheme, setMcTheme] = useState(() => {
    if (typeof window === "undefined") return "dark";
    try { return window.localStorage.getItem("mc-theme") || "dark"; } catch (_) { return "dark"; }
  });
  const isLightTheme = mcTheme === "light";
  const engine = mcSyncReady ? (loadEngineState("ALL") || buildDefaultState()) : buildDefaultState();
  const activeAllSnapshot = mcSyncReady ? peekActiveInventorySnapshot("ALL") : null;
  const adminStoreView = useAdminStoreView();

  const storeOptions = useMemo(() => {
    if (!mcSyncReady) return [];
    // The Sites registry is the single source of truth for the global selector.
    // Every active site shown in Sites must also be selectable everywhere else,
    // regardless of whether it was created manually, recovered from history, or
    // approved from CSV Mapping.
    const registrySites = loadSites()
      .filter((site) => site?.active !== false)
      .map((site) => site.siteId || site.storeKey || inventoryHeaderFromSiteName(site.name))
      .filter(Boolean);

    // The active All Sites inventory is already hydrated before the durable Sites
    // registry finishes settling after a refresh. Use its inventory_* headers as
    // an immediate, read-only selector source so the global dropdown never
    // temporarily collapses to the one site currently present in mc-sites. The
    // registry still remains authoritative for activation/permissions; these
    // headers only fill identities that are demonstrably present in the current
    // organisation's active inventory.
    const selectorHeaders = Array.from(new Set([
      ...(Array.isArray(activeAllSnapshot?.parsedHeaders) ? activeAllSnapshot.parsedHeaders : []),
      ...(Array.isArray(engine?.parsedHeaders) ? engine.parsedHeaders : []),
      ...(Array.isArray(durableAllHeaders) ? durableAllHeaders : []),
    ]));
    const inventorySites = detectSiteHeaders(selectorHeaders)
      .map((header) => {
        const resolved = resolveSite(header) || resolveSite(labelFromSiteValue(header));
        return resolved?.siteId || resolved?.storeKey || header;
      })
      .filter(Boolean);

    const uniqueSites = Array.from(new Set([...registrySites, ...inventorySites]));
    if (!currentProfile || isAdminProfile(currentProfile)) return ['ALL', ...uniqueSites];

    const allowedRaw = currentProfile.allowedSiteIds || currentProfile.allowedSites || [];
    const allowed = new Set(
      (Array.isArray(allowedRaw) ? allowedRaw : [])
        .map((value) => {
          const site = resolveSite(value);
          return site?.siteId || String(value || '').trim();
        })
        .filter(Boolean)
    );

    const primarySite = currentProfile.siteId || currentProfile.storeKey || currentProfile.store;
    if (primarySite) {
      const site = resolveSite(primarySite);
      allowed.add(site?.siteId || String(primarySite || '').trim());
    }

    return uniqueSites.filter((option) => {
      const site = resolveSite(option);
      return allowed.has(site?.siteId || String(option || '').trim());
    });
  }, [mcSyncReady, currentProfile, engine?.parsedHeaders, activeAllSnapshot?.parsedHeaders, durableAllHeaders, siteVersion]);

  const displayName = useMemo(() => {
    if (currentProfile) return buildProfileDisplayName(currentProfile);
    const raw = claims?.displayName || user?.displayName || user?.email || "User";
    return String(raw).split("@")[0];
  }, [claims, currentProfile, user]);

  const isRealAdminProfile = useMemo(() => {
    if (!isAdminProfile(currentProfile)) return false;
    const id = String(currentProfile?.id || "");
    const email = String(currentProfile?.email || currentProfile?.userEmail || "").trim();
    // Only the authenticated admin profile may jump back into Neo. Store/operator
    // profiles must stay inside MC even if the Firebase user has Neo access.
    return Boolean(email) || id === "mc-admin" || id.startsWith("mc-admin-") || id === "admin-profile";
  }, [currentProfile]);

  const canSwitchToNeo = isRealAdminProfile && hasExplicitNeoAccess(claims || {});
  const canSwitchToLeague = hasLeagueAccess(claims || {}) || hasLeagueAccess(member || {}) || hasLeagueAccess(currentProfile || {});

  const liveSessionRouteMatch = useMemo(() => {
    const path = String(location?.pathname || "").replace(/\/$/, "");
    const match = path.match(/^\/morpheus-count\/session\/([^/]+)$/);
    return match ? decodeURIComponent(match[1] || "") : "";
  }, [location?.pathname]);

  const trafficProbeContext = useMemo(() => getMcTrafficProbeRouteContext({
    pathname: location?.pathname || "",
    hash: location?.hash || "",
  }), [location?.pathname, location?.hash]);

  useEffect(() => {
    if (!mcSyncReady) {
      setDataSettling(true);
      return undefined;
    }

    // Keep the indicator tied to the data that the current workspace actually
    // renders. mcSyncReady means Firestore has hydrated the organisation cache,
    // but Sessions/Archives can still be consuming their own session snapshot for
    // a few more seconds. Do not hide the indicator on a fixed timer before that
    // page-level data has arrived.
    setDataSettling(true);

    const path = String(location?.pathname || "");
    const isArchiveRoute = path.includes("/morpheus-count/archive");
    const isSessionsRoute = path.includes("/morpheus-count/sessions");
    const startedAt = Date.now();
    let settleTimer = null;
    let pollTimer = null;
    let pageSnapshotSeen = false;

    const finishAfterPaint = () => {
      if (pageSnapshotSeen) return;
      pageSnapshotSeen = true;
      window.clearTimeout(settleTimer);
      // Give the route component time to render the snapshot before removing the
      // cue, avoiding the brief 0-card flash that made slow Wi-Fi look broken.
      settleTimer = window.setTimeout(() => setDataSettling(false), 450);
    };

    const onSessionSnapshot = (event) => {
      const type = String(event?.detail?.type || "");
      if (isArchiveRoute && type === "archived") finishAfterPaint();
      else if (isSessionsRoute && type === "active") finishAfterPaint();
      else if (!isArchiveRoute && !isSessionsRoute && (type === "active" || type === "archived" || type === "detail")) finishAfterPaint();
    };

    window.addEventListener(MC_SESSION_EVENT, onSessionSnapshot);

    // Cached data can already be present before this effect attaches. Poll only
    // for that case; this does not change or trigger the existing data pipeline.
    pollTimer = window.setInterval(() => {
      if (pageSnapshotSeen) return;
      try {
        if (isArchiveRoute && loadArchivedSessionRecords().length > 0) finishAfterPaint();
        else if (isSessionsRoute && loadSessionRecords().length > 0) finishAfterPaint();
      } catch (_) {}
    }, 150);

    // Empty organisations are valid. If no session snapshot event is emitted, keep
    // a generous safety stop so the indicator never becomes permanent.
    const hardStop = window.setTimeout(() => setDataSettling(false), 12000);

    // Non-session pages do not need to wait for session lists. Their visible data
    // is ready once the organisation snapshot has had one render pass.
    if (!isArchiveRoute && !isSessionsRoute) {
      settleTimer = window.setTimeout(() => finishAfterPaint(), 700);
    }

    return () => {
      window.removeEventListener(MC_SESSION_EVENT, onSessionSnapshot);
      window.clearInterval(pollTimer);
      window.clearTimeout(settleTimer);
      window.clearTimeout(hardStop);
    };
  }, [mcSyncReady, location?.pathname]);

  useEffect(() => {
    if (!mcSyncReady) {
      setDurableAllHeaders([]);
      return undefined;
    }

    let cancelled = false;
    const adoptSnapshotHeaders = (snapshot) => {
      if (cancelled) return;
      const headers = Array.isArray(snapshot?.parsedHeaders) ? snapshot.parsedHeaders : [];
      // The active ALL inventory snapshot is the durable inventory authority and
      // already carries every inventory_<site> column. Promote those identities
      // into the shared org site registry so Imports, Prepare Count, Sessions and
      // every other tab all consume the same site source instead of waiting for
      // Intake/Review to mount.
      if (detectSiteHeaders(headers).length) ensureSitesFromHeaders(headers);
      setDurableAllHeaders((current) => {
        const next = Array.from(new Set(headers.filter(Boolean)));
        if (current.length === next.length && current.every((value, index) => value === next[index])) return current;
        return next;
      });
    };

    // The global site selector is shared by every Morpheus tab, so it cannot
    // depend on Intake/Review mounting first. Hydrate the active ALL inventory
    // snapshot here at chrome level: local IndexedDB/cache resolves immediately
    // after refresh and Firestore refreshes it in the background when needed.
    loadActiveInventorySnapshot("ALL", { refreshCloud: false })
      .then(adoptSnapshotHeaders)
      .catch(() => null);
    loadActiveInventorySnapshot("ALL", { refreshCloud: true })
      .then(adoptSnapshotHeaders)
      .catch(() => null);

    const onInventoryChanged = (event) => {
      const scope = String(event?.detail?.scope || "ALL").toUpperCase();
      if (scope !== "ALL") return;
      const snapshot = peekActiveInventorySnapshot("ALL");
      if (snapshot) adoptSnapshotHeaders(snapshot);
      else loadActiveInventorySnapshot("ALL", { refreshCloud: false }).then(adoptSnapshotHeaders).catch(() => null);
    };
    window.addEventListener(MC_ACTIVE_INVENTORY_EVENT, onInventoryChanged);
    return () => {
      cancelled = true;
      window.removeEventListener(MC_ACTIVE_INVENTORY_EVENT, onInventoryChanged);
    };
  }, [mcSyncReady]);

  useEffect(() => {
    if (!mcSyncReady) return;
    // mc-sites may be absent in older org snapshots even though imports, sessions,
    // and archives still contain the site identities. loadSites() recovers those
    // identities from operational data; persist the recovered registry once so
    // future refreshes no longer depend on importing again.
    const registrySites = loadSites();
    if (registrySites.length) saveSites(registrySites);
  }, [mcSyncReady]);

  useEffect(() => {
    // Operational readers can legitimately publish MC site/sync/storage events
    // while a route is still rendering. Never mutate CountChrome React state
    // synchronously from those cross-route events; defer and coalesce them so
    // React finishes the active render before the shared site selector refreshes.
    let cancelled = false;
    let timer = null;

    function flushSites() {
      timer = null;
      if (cancelled) return;
      setSiteVersion((version) => version + 1);
    }

    function syncSites() {
      if (cancelled || timer !== null) return;
      timer = window.setTimeout(flushSites, 0);
    }

    window.addEventListener(MC_SITE_EVENT, syncSites);
    window.addEventListener(MC_SYNC_EVENT, syncSites);
    window.addEventListener(MC_ACTIVE_INVENTORY_EVENT, syncSites);
    window.addEventListener("storage", syncSites);
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
      window.removeEventListener(MC_SITE_EVENT, syncSites);
      window.removeEventListener(MC_SYNC_EVENT, syncSites);
      window.removeEventListener(MC_ACTIVE_INVENTORY_EVENT, syncSites);
      window.removeEventListener("storage", syncSites);
    };
  }, []);

  useEffect(() => {
    if (!storeOptions.length) return;
    if (!storeOptions.includes(adminStoreView)) {
      const fallback = storeOptions.includes('ALL') ? 'ALL' : storeOptions[0];
      setAdminStoreView(fallback);
    }
  }, [adminStoreView, storeOptions]);

  useLayoutEffect(() => {
    if (typeof document === "undefined") return;
    const html = document.documentElement;
    const body = document.body;
    html.classList.remove("mc-theme-light", "mc-theme-dark");
    body.classList.remove("mc-theme-light", "mc-theme-dark");
    html.dataset.mcTheme = mcTheme;
    body.dataset.mcTheme = mcTheme;
    html.classList.add(isLightTheme ? "mc-theme-light" : "mc-theme-dark");
    body.classList.add(isLightTheme ? "mc-theme-light" : "mc-theme-dark");
    html.style.colorScheme = isLightTheme ? "light" : "dark";
    body.style.colorScheme = isLightTheme ? "light" : "dark";
    try { window.localStorage.setItem("mc-theme", mcTheme); } catch (_) {}
    return () => {
      html.classList.remove("mc-theme-light", "mc-theme-dark");
      body.classList.remove("mc-theme-light", "mc-theme-dark");
      delete html.dataset.mcTheme;
      delete body.dataset.mcTheme;
      html.style.colorScheme = "";
      body.style.colorScheme = "";
    };
  }, [isLightTheme, mcTheme]);

  function toggleMcTheme() {
    setMcTheme((theme) => theme === "light" ? "dark" : "light");
  }

  async function refreshCurrentMorpheusView() {
    if (softRefreshing) return;
    setSoftRefreshing(true);
    try {
      // Refresh the data sources in-place. Do not reload the document: a hard
      // reload remounts the access gate and unnecessarily sends an already
      // authenticated operator back through the Morpheus access layer.
      const scope = adminStoreView || "ALL";
      await Promise.allSettled([
        loadActiveInventorySnapshot("ALL", { refreshCloud: true }),
        scope !== "ALL" ? loadActiveInventorySnapshot(scope, { refreshCloud: true }) : Promise.resolve(null),
      ]);

      setSiteVersion((value) => value + 1);
      window.dispatchEvent(new CustomEvent(MC_SYNC_EVENT, { detail: { source: "manual-soft-refresh" } }));
      window.dispatchEvent(new CustomEvent(MC_ACTIVE_INVENTORY_EVENT, { detail: { source: "manual-soft-refresh", scope } }));
      window.dispatchEvent(new CustomEvent(MC_SESSION_EVENT, { detail: { source: "manual-soft-refresh" } }));
    } finally {
      // Give route subscribers one paint to consume the refresh events before
      // the control becomes active again.
      window.setTimeout(() => setSoftRefreshing(false), 450);
    }
  }

  useEffect(() => {
    if (!menuOpen) return;
    function handleOutside(event) {
      const target = event.target;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setMenuOpen(false);
      setHoveredAction("");
    }
    function handleEscape(event) {
      if (event.key === "Escape") {
        setMenuOpen(false);
        setHoveredAction("");
      }
    }
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [menuOpen]);

  useEffect(() => {
    const idleMs = 10 * 60 * 1000;
    let timerId;

    const resetIdleTimer = () => {
      window.clearTimeout(timerId);
      timerId = window.setTimeout(() => {
        clearCurrentProfile();
        setAdminStoreView("ALL");
        window.location.assign("/morpheus-count/workspace");
      }, idleMs);
    };

    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];
    events.forEach((eventName) => window.addEventListener(eventName, resetIdleTimer, { passive: true }));
    resetIdleTimer();

    return () => {
      window.clearTimeout(timerId);
      events.forEach((eventName) => window.removeEventListener(eventName, resetIdleTimer));
    };
  }, []);

  if (!mcSyncReady) {
    return (
      <div className={`mc-engineShell${isLightTheme ? " mc-engineShell--light" : ""}`}>
        <div className="mc-syncHydrate" style={{ minHeight: "100vh", display: "grid", placeItems: "center", color: isLightTheme ? "#082033" : "#e0f2fe" }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 42, fontWeight: 900, letterSpacing: ".08em" }}>Morpheus</div>
            <div style={{ width: 220, height: 4, margin: "22px auto", borderRadius: 999, background: "linear-gradient(90deg, rgba(34,211,238,.15), rgba(34,211,238,.9), rgba(34,211,238,.15))" }} />
            <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: ".38em", textTransform: "uppercase", color: isLightTheme ? "#0891b2" : "#67e8f9" }}>Syncing organisation data</div>
          </div>
        </div>
      </div>
    );
  }

  async function doLogout() {
    if (busy) return;

    setBusy(true);
    try {
      clearCurrentProfile();
      clearActiveApp();
      await signOut(getAuthClient());
    } finally {
      window.location.assign("/login");
    }
  }

  function switchToNeo() {
    if (!canSwitchToNeo) return;
    setMenuOpen(false);
    setHoveredAction("");
    setActiveApp("neo");
    navigate("/cashup");
  }

  function switchToLeague() {
    if (!canSwitchToLeague) return;
    setMenuOpen(false);
    setHoveredAction("");
    setActiveApp("league");
    navigate("/league", { replace: true });
  }

  const menuItemBase = {
    width: "100%",
    textAlign: "left",
    padding: "20px 24px",
    background: "transparent",
    border: 0,
    cursor: "pointer",
    transition: "background .18s ease, box-shadow .18s ease, transform .18s ease",
  };

  return (
    <div className={isLightTheme ? "mc-theme-light" : "mc-theme-dark"} style={{ position: "relative" }}>
      <div
        className="mc-mobileTopControls"
        style={{
          position: "absolute",
          top: 24,
          right: 20,
          zIndex: 60,
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          maxWidth: "calc(100vw - 48px)",
        }}
      >
        {storeOptions.length > 0 && (
          <select
            value={storeOptions.includes(adminStoreView) ? adminStoreView : storeOptions[0]}
            onChange={(e) => {
              setAdminStoreView(e.target.value);
            }}
            style={{
              minWidth: 180,
              border: isLightTheme ? "1px solid rgba(14,116,144,.18)" : "1px solid rgba(103,232,249,.18)",
              background: isLightTheme ? "rgba(255,255,255,.94)" : "rgba(8,20,40,.9)",
              color: isLightTheme ? "#082033" : "#f8fafc",
              borderRadius: 999,
              padding: "12px 18px",
              fontWeight: 700,
              fontSize: 14,
              boxShadow: isLightTheme ? "0 10px 26px rgba(15,23,42,.08)" : "0 0 24px rgba(34, 211, 238, 0.08)",
              backdropFilter: "blur(14px)",
            }}
          >
            {storeOptions.map((option) => (
              <option key={option} value={option}>
                {formatStoreLabel(option)}
              </option>
            ))}
          </select>
        )}

        <button
          type="button"
          className={`mc-globalRefreshButton${softRefreshing ? " is-refreshing" : ""}`}
          onClick={refreshCurrentMorpheusView}
          disabled={softRefreshing}
          aria-label="Refresh Morpheus data"
          title="Refresh this Morpheus page"
        >
          <span aria-hidden="true">↻</span>
        </button>

        <button
          type="button"
          onClick={toggleMcTheme}
          aria-label={isLightTheme ? "Switch to dark theme" : "Switch to light theme"}
          title={isLightTheme ? "Switch to dark theme" : "Switch to light theme"}
          style={{
            width: 48,
            height: 48,
            borderRadius: 999,
            border: isLightTheme ? "1px solid rgba(14,116,144,.16)" : "1px solid rgba(103,232,249,.18)",
            background: isLightTheme ? "rgba(255,255,255,.95)" : "rgba(8,20,40,.9)",
            color: isLightTheme ? "#082033" : "#f8fafc",
            fontWeight: 900,
            fontSize: 18,
            cursor: "pointer",
            boxShadow: isLightTheme ? "0 10px 26px rgba(15,23,42,.08)" : "0 0 24px rgba(34,211,238,.10)",
            backdropFilter: "blur(14px)",
          }}
        >
          {isLightTheme ? "☾" : "☀"}
        </button>

        <div className={isLightTheme ? "mc-theme-light" : "mc-theme-dark"} style={{ position: "relative" }}>
          <button
            ref={buttonRef}
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            style={{
              minWidth: 160,
              border: isLightTheme ? "1px solid rgba(14,116,144,.18)" : "1px solid rgba(103, 232, 249, 0.18)",
              background: isLightTheme ? "rgba(255,255,255,.95)" : (menuOpen ? "rgba(6,18,36,.95)" : "rgba(8, 20, 40, 0.9)"),
              color: isLightTheme ? "#082033" : "#f8fafc",
              borderRadius: 999,
              padding: "12px 18px",
              fontWeight: 800,
              fontSize: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
          maxWidth: "calc(100vw - 48px)",
              boxShadow: isLightTheme ? "0 10px 26px rgba(15,23,42,.08)" : (menuOpen ? "0 0 28px rgba(34, 211, 238, 0.16)" : "0 0 24px rgba(34, 211, 238, 0.12)"),
              cursor: "pointer",
              backdropFilter: "blur(14px)",
            }}
          >
            <span>{displayName}</span>
            <span style={{ fontSize: 12, opacity: 0.75 }}>{menuOpen ? "▴" : "▾"}</span>
          </button>

          {dataSettling && !menuOpen ? (
            <div className="mc-dataLoadingIndicator" role="status" aria-live="polite">
              <span className="mc-dataLoadingIndicator__spinner" aria-hidden="true" />
              <span>Loading data…</span>
            </div>
          ) : null}

          {menuOpen && (
            <div
              ref={menuRef}
              style={{
                position: "absolute",
                top: "calc(100% + 10px)",
                right: 0,
                width: 280,
                borderRadius: 26,
                overflow: "hidden",
                border: isLightTheme ? "1px solid rgba(14,116,144,.14)" : "1px solid rgba(103, 232, 249, 0.12)",
                background: isLightTheme ? "linear-gradient(180deg, rgba(255,255,255,.98), rgba(239,248,252,.99))" : "linear-gradient(180deg, rgba(8,20,40,.98), rgba(4,13,27,.99))",
                boxShadow: isLightTheme ? "0 24px 70px rgba(15,23,42,.14)" : "0 24px 90px rgba(0,0,0,.45)",
                backdropFilter: "blur(18px)",
                zIndex: 61,
              }}
            >
              <button
                type="button"
                onClick={switchToNeo}
                disabled={!canSwitchToNeo}
                onMouseEnter={() => canSwitchToNeo && setHoveredAction("neo")}
                onMouseLeave={() => setHoveredAction("")}
                style={{
                  ...menuItemBase,
                  color: canSwitchToNeo ? (isLightTheme ? "#082033" : "#f8fafc") : (isLightTheme ? "rgba(8,32,51,.38)" : "rgba(248,250,252,.45)"),
                  cursor: canSwitchToNeo ? "pointer" : "not-allowed",
                  opacity: canSwitchToNeo ? 1 : 0.58,
                  background: hoveredAction === "neo" ? "linear-gradient(180deg, rgba(34,211,238,.12), rgba(8,20,40,.14))" : "transparent",
                  boxShadow: hoveredAction === "neo" ? "inset 0 0 0 1px rgba(34,211,238,.14), 0 0 28px rgba(34,211,238,.16)" : "none",
                }}
              >
                <div style={{ fontSize: 18, fontWeight: 800 }}>Switch to Neo</div>
                <div style={{ marginTop: 6, fontSize: 14, color: isLightTheme ? (hoveredAction === "neo" ? "#0284c7" : "#45647d") : (hoveredAction === "neo" ? "#67e8f9" : "rgba(186,230,253,.85)") }}>
                  Operations layer
                </div>
              </button>

              <div style={{ height: 1, background: isLightTheme ? "rgba(8,32,51,.08)" : "rgba(255,255,255,.08)" }} />

              <button
                type="button"
                onClick={switchToLeague}
                disabled={!canSwitchToLeague}
                onMouseEnter={() => canSwitchToLeague && setHoveredAction("league")}
                onMouseLeave={() => setHoveredAction("")}
                style={{
                  ...menuItemBase,
                  color: canSwitchToLeague ? (isLightTheme ? "#082033" : "#f8fafc") : (isLightTheme ? "rgba(8,32,51,.38)" : "rgba(248,250,252,.45)"),
                  cursor: canSwitchToLeague ? "pointer" : "not-allowed",
                  opacity: canSwitchToLeague ? 1 : 0.58,
                  background: hoveredAction === "league" ? "linear-gradient(180deg, rgba(250,204,21,.12), rgba(8,20,40,.14))" : "transparent",
                  boxShadow: hoveredAction === "league" ? "inset 0 0 0 1px rgba(250,204,21,.18), 0 0 28px rgba(250,204,21,.14)" : "none",
                }}
              >
                <div style={{ fontSize: 18, fontWeight: 800 }}>Switch to League</div>
                <div style={{ marginTop: 6, fontSize: 14, color: isLightTheme ? (hoveredAction === "league" ? "#a16207" : "#45647d") : (hoveredAction === "league" ? "#fde68a" : "rgba(186,230,253,.85)") }}>
                  {canSwitchToLeague ? "Competition layer" : "League access required"}
                </div>
              </button>

              <div style={{ height: 1, background: isLightTheme ? "rgba(8,32,51,.08)" : "rgba(255,255,255,.08)" }} />

              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  setHoveredAction("");
                  clearCurrentProfile();
                  clearRememberedOperationalDate();
                  try { window.sessionStorage?.removeItem('mc-access-layer-unlocked'); } catch (_) {}
                  try { window.dispatchEvent(new CustomEvent('mc-profile-switch-user')); } catch (_) {}
                  navigate("/morpheus-count/workspace", { replace: true });
                }}
                onMouseEnter={() => setHoveredAction("switch-user")}
                onMouseLeave={() => setHoveredAction("")}
                style={{
                  ...menuItemBase,
                  color: isLightTheme ? "#082033" : "#f8fafc",
                  background: hoveredAction === "switch-user" ? "linear-gradient(180deg, rgba(34,211,238,.10), rgba(8,20,40,.14))" : "transparent",
                  boxShadow: hoveredAction === "switch-user" ? "inset 0 0 0 1px rgba(34,211,238,.14), 0 0 28px rgba(34,211,238,.16)" : "none",
                }}
              >
                <div style={{ fontSize: 18, fontWeight: 800 }}>Switch User</div>
                <div style={{ marginTop: 6, fontSize: 14, color: isLightTheme ? (hoveredAction === "switch-user" ? "#0284c7" : "#45647d") : (hoveredAction === "switch-user" ? "#67e8f9" : "rgba(186,230,253,.85)") }}>
                  Return to access layer
                </div>
              </button>

              <div style={{ height: 1, background: isLightTheme ? "rgba(8,32,51,.08)" : "rgba(255,255,255,.08)" }} />

              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  setHoveredAction("");
                  setShowLogoutConfirm(true);
                }}
                onMouseEnter={() => setHoveredAction("logout")}
                onMouseLeave={() => setHoveredAction("")}
                style={{
                  ...menuItemBase,
                  color: isLightTheme ? "#082033" : "#f8fafc",
                  background: hoveredAction === "logout" ? "linear-gradient(180deg, rgba(239,68,68,.10), rgba(8,20,40,.14))" : "transparent",
                  boxShadow: hoveredAction === "logout" ? "inset 0 0 0 1px rgba(239,68,68,.14), 0 0 28px rgba(239,68,68,.16)" : "none",
                }}
              >
                <div style={{ fontSize: 18, fontWeight: 800, color: isLightTheme ? (hoveredAction === "logout" ? "#b91c1c" : "#082033") : (hoveredAction === "logout" ? "#fecaca" : "#f8fafc") }}>Logout</div>
                <div style={{ marginTop: 6, fontSize: 14, color: isLightTheme ? (hoveredAction === "logout" ? "#dc2626" : "#45647d") : (hoveredAction === "logout" ? "rgba(252,165,165,.92)" : "rgba(186,230,253,.85)") }}>
                  End this session
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      {showLogoutConfirm && (
        <div style={{ position: "fixed", inset: 0, zIndex: 2147483647, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div
            onClick={() => setShowLogoutConfirm(false)}
            style={{
              position: "absolute",
              inset: 0,
              background: isLightTheme ? "rgba(15,23,42,.42)" : "rgba(2,6,23,.58)",
              backdropFilter: "blur(12px)",
            }}
          />
          <div
            style={{
              position: "relative",
              width: "min(520px, 92vw)",
              borderRadius: 34,
              padding: 28,
              border: isLightTheme ? "1px solid rgba(14,116,144,.16)" : "1px solid rgba(103,232,249,.16)",
              background: isLightTheme
                ? "linear-gradient(180deg, rgba(255,255,255,.98), rgba(239,248,252,.99))"
                : "linear-gradient(180deg, rgba(8,20,40,.96), rgba(4,13,27,.99))",
              boxShadow: isLightTheme ? "0 30px 90px rgba(15,23,42,.18)" : "0 30px 120px rgba(0,0,0,.55)",
              color: isLightTheme ? "#082033" : "#f8fafc",
            }}
          >
            <div style={{ fontSize: 12, letterSpacing: ".35em", textTransform: "uppercase", color: isLightTheme ? "#0284c7" : "rgba(186,230,253,.9)" }}>Session control</div>
            <div style={{ marginTop: 12, fontSize: 26, fontWeight: 900, color: isLightTheme ? "#082033" : "#f8fafc" }}>Log out of Morpheus Count?</div>
            <p style={{ marginTop: 14, fontSize: 16, lineHeight: 1.8, color: isLightTheme ? "#2d5d7b" : "rgba(226,232,240,.88)" }}>
              You will be returned to the MatrixOS login page and this Morpheus session will close.
            </p>
            <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end", gap: 12 }}>
              <button
                type="button"
                onClick={() => setShowLogoutConfirm(false)}
                style={{
                  borderRadius: 999,
                  border: isLightTheme ? "1px solid rgba(14,116,144,.18)" : "1px solid rgba(255,255,255,.12)",
                  background: isLightTheme ? "rgba(255,255,255,.96)" : "rgba(255,255,255,.02)",
                  color: isLightTheme ? "#082033" : "#fff",
                  padding: "12px 20px",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button type="button" onClick={doLogout} disabled={busy} style={{ borderRadius: 999, border: 0, background: "#22d3ee", color: "#020617", padding: "12px 20px", fontWeight: 800, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.7 : 1, boxShadow: isLightTheme ? "0 14px 34px rgba(34,211,238,.22)" : "none" }}>
                {busy ? "Signing out..." : "Logout"}
              </button>
            </div>
          </div>
        </div>
      )}

      {liveSessionRouteMatch ? <MorpheusCountSessionDetail key={liveSessionRouteMatch} /> : <>{children}</>}

      {/* MC492: Live Count keeps its specialised in-page probe. Every other
          Morpheus route gets one global floating inspector from the shell. */}
      {!liveSessionRouteMatch ? (
        <McDevTrafficPanel
          probeId={trafficProbeContext.probeId}
          title={trafficProbeContext.title}
          subtitle={trafficProbeContext.subtitle}
          badgeLabel={trafficProbeContext.label}
          mode="floating"
        />
      ) : null}
    </div>
  );
}
